import React from 'react';

import evaluateExpression from 'services/numbers/evaluateExpression';
import exceedsIntegerDigits from 'services/numbers/exceedsIntegerDigits';
import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';

// The result of a calculation typed into a field is rounded to this many decimals
const CALCULATED_DECIMALS = 4;

/**
 * The handlers of a number field into which a calculation can be typed, e.g. 2+2: it is
 * replaced with its result (4) when Enter is pressed or the field is left. Typing more
 * digits before the decimals than maxIntegerDigits is ignored; a calculation being typed
 * is checked by its result.
 */
const getCalculatingInputHandlers = ({
  disabled,
  maxIntegerDigits,
  name,
  onBlur,
  onChange,
  value,
}: {
  disabled?: boolean;
  maxIntegerDigits?: number;
  name?: string;
  onBlur?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (event: any) => void;
  value: string;
}): {
  onBlur: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
} => {
  // True when the field held a calculation and its result was set
  const calculate = (): boolean => {
    if (disabled) {
      return false;
    }

    const result = evaluateExpression(value);
    if (result === undefined) {
      return false;
    }

    const text = formatFloatForDisplay(result, CALCULATED_DECIMALS);
    if (exceedsIntegerDigits(text, maxIntegerDigits)) {
      return false;
    }

    // Read by the handlers of the fields as a change event
    onChange?.({ target: { name, value: text } });
    return true;
  };

  return {
    onBlur: () => {
      calculate();
      onBlur?.();
    },
    onChange: (event) => {
      const isCalculation = /[-+*/()=]/.test(event.target.value.slice(1));
      if (
        isCalculation ||
        !exceedsIntegerDigits(event.target.value, maxIntegerDigits)
      ) {
        onChange?.(event);
      }
    },
    onKeyDown: (event) => {
      if (event.key === 'Enter' && calculate()) {
        event.preventDefault();
      }
    },
  };
};

export default getCalculatingInputHandlers;
